import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { authApi } from '../../api/endpoints.js';
import { PublicShell } from '../../app/PublicShell.js';
import {
  ActionButton,
  Alert,
  ErrorState,
  Form,
  TextField,
} from '../../components/ui/index.js';

/**
 * Request a password-reset message.
 *
 * The confirmation says a message has been sent IF the address is registered.
 * That is the literal truth — the server answers identically either way — and
 * it is also the only wording that does not turn this page into a way to test
 * whether someone works at the laboratory.
 *
 * It also honours the honesty rule about sending: the confirmation appears only
 * after the request RESOLVED, never on the press of the button.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await authApi.requestPasswordReset(email);
      setIsSent(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setIsSubmitting(false);
    }
  };

  const backToSignIn = (
    <Link
      to="/login"
      className="rounded-sm text-center text-body text-primary-800 hover:underline"
    >
      {t('auth.backToSignIn')}
    </Link>
  );

  return (
    <PublicShell
      title={t('auth.forgotTitle')}
      subtitle={t('auth.forgotSubtitle')}
    >
      {isSent ? (
        <div className="flex flex-col gap-stack">
          <Alert tone="success" title={t('auth.forgotSent')} />
          {backToSignIn}
        </div>
      ) : (
        <Form onSubmit={handleSubmit} isSubmitting={isSubmitting}>
          <TextField
            type="email"
            name="email"
            label={t('auth.email')}
            placeholder={t('auth.emailPlaceholder')}
            value={email}
            onChange={setEmail}
            autoComplete="username"
            isRequired
          />

          {error ? <ErrorState error={error} /> : null}

          <ActionButton
            priority="primary"
            type="submit"
            isLoading={isSubmitting}
            isDisabled={!email}
            fullWidth
          >
            {t('auth.forgotSubmit')}
          </ActionButton>

          {backToSignIn}
        </Form>
      )}
    </PublicShell>
  );
}
